/**
 * EL PAGO DE NEXO SE REGISTRA POR CLIENTE (PAGOS-NEXO-POR-CLIENTE-T1, Josse 6-oct via Ares #22467).
 *
 * DE DONDE SALE. Josse: "el artefacto para registrar pagos de Nexo tiene un bug, no permite poner
 * un monto, y es demasiado complicado tener que copiar el nombre completo de los negocios".
 * Medido el 6-oct en el navegador sobre NexoPagosView.js (main a083826):
 *   - el monto era <input type=number min=1 step=1000>: los valores validos eran 1, 1001, 2001...
 *     y el navegador rechazaba 75000 ("los dos valores validos mas aproximados son 75001 y 76001");
 *     ademas "75.000" se leia como 75. Ningun monto redondo pasaba: ese era el "no deja poner".
 *   - el negocio era texto libre y el pago viajaba sin businessId (la API ya lo recibe).
 *   - cambiar la fecha pisaba el mes cubierto aunque ya lo hubieran elegido.
 *
 * LAS REGLAS:
 *   1. el negocio sale de la lista de negocios ACTIVOS y el pago lleva su business_id; sin negocio
 *      de la lista no se registra.
 *   2. el monto se escribe como se escribe en Colombia (75.000, $75.000, 75000) y arranca en la
 *      tarifa de la suscripcion del negocio; si paga menos, el descuento queda dicho en el pago.
 *   3. la fecha solo propone el mes cubierto mientras nadie lo haya tocado.
 *
 * Sin DOM: lo prueba scripts/check-pago-nexo.mjs.
 */

import { ordenarNegocios } from './ordenNegocios.js';
import { idDelNegocio } from './idDelNegocio.js';

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** La lista que llega del API, venga suelta o en su sobre. */
function lista(res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const adentro = d?.data ?? d;
  const items = adentro?.items ?? adentro;
  return Array.isArray(items) ? items : [];
}

/** El estado del negocio en minusculas: el API manda el enum como "Active" y la base como "active". */
const estadoDe = (b) => texto(b?.status ?? b?.Status).toLowerCase();

/**
 * Los negocios a los que se les puede registrar un pago: solo activos y con id, en orden alfabetico.
 * @returns {{id: string, nombre: string}[]}
 */
export function negociosParaPago(resONegocios) {
  const activos = lista(resONegocios).filter((b) => estadoDe(b) === 'active' && idDelNegocio(b) !== '');
  return ordenarNegocios(activos).map((b) => ({
    id: idDelNegocio(b),
    nombre: texto(b.displayName ?? b.DisplayName ?? b.display_name) || idDelNegocio(b)
  }));
}

/**
 * El monto escrito a mano, en pesos enteros. En Colombia el punto separa miles ("75.000"); una coma
 * o un punto seguido de 1 o 2 cifras al final son centavos y se descartan (el monto es entero).
 * @returns {number} el monto, o 0 si no hay numero.
 */
export function leerMonto(escrito) {
  let t = texto(escrito).replace(/[$\s]/g, '');
  t = t.replace(/[.,]\d{1,2}$/, '');
  const digitos = t.replace(/\D/g, '');
  return digitos ? Number(digitos) : 0;
}

/** El monto como se lee en pantalla: 75000 -> "75.000". */
export function montoParaVer(n) {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(v) : '';
}

const fechaDe = (s) => {
  const t = Date.parse(texto(s?.startDate ?? s?.StartDate ?? s?.start_date));
  return Number.isFinite(t) ? t : 0;
};

/**
 * La tarifa del negocio: el monto de su suscripcion activa mas reciente; si no tiene activa, la mas
 * reciente que tenga. 0 si no hay ninguna con monto.
 */
export function tarifaDeSuscripciones(resOSuscripciones) {
  const subs = lista(resOSuscripciones)
    .map((s) => ({ s, monto: Number(s?.amount ?? s?.Amount ?? 0), activa: texto(s?.status ?? s?.Status).toLowerCase() === 'active' }))
    .filter((x) => x.monto > 0)
    .sort((a, b) => (Number(b.activa) - Number(a.activa)) || (fechaDe(b.s) - fechaDe(a.s)));
  return subs.length ? subs[0].monto : 0;
}

/** El mes que queda al cambiar la fecha: la fecha lo propone solo si nadie toco el mes. */
export function mesQueQueda(fecha, mesActual, mesTocado) {
  if (mesTocado && texto(mesActual)) return texto(mesActual);
  const f = texto(fecha);
  return /^\d{4}-\d{2}/.test(f) ? f.slice(0, 7) : texto(mesActual);
}

/** Ultimo dia del mes cubierto (YYYY-MM -> YYYY-MM-DD), para decir hasta cuando queda activa. */
export function finDelMes(mes) {
  const m = /^(\d{4})-(\d{2})$/.exec(texto(mes));
  if (!m) return '';
  const ultimo = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return `${m[1]}-${m[2]}-${String(ultimo).padStart(2, '0')}`;
}

/**
 * El cuerpo del pago, armado en un solo sitio, o el error que lo frena.
 * @param {{negocio: {id: string, nombre: string}|null, montoEscrito: string, tarifa: number, medio: string,
 *          fechaPago: string, mesCubierto: string, canal: string, notas: string, motivoDescuento: string}} f
 * @returns {{ok: true, cuerpo: object} | {ok: false, error: string, campo: string}}
 */
export function cuerpoDelPago(f) {
  const negocio = f?.negocio;
  if (!negocio || !texto(negocio.id)) return { ok: false, campo: 'negocio', error: 'Elige el negocio de la lista' };
  const monto = leerMonto(f.montoEscrito);
  if (!(monto > 0)) return { ok: false, campo: 'monto', error: 'Escribe el monto que pago (por ejemplo 75.000)' };
  const fechaPago = texto(f.fechaPago);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaPago)) return { ok: false, campo: 'fecha', error: 'La fecha del pago es obligatoria' };

  const tarifa = Number(f.tarifa) > 0 ? Number(f.tarifa) : 0;
  const descuento = tarifa > monto ? tarifa - monto : 0;
  const motivo = texto(f.motivoDescuento);
  // La API (PAGOS-NEXO-ACTIVAR-API-T1) frena un descuento sin motivo (PAGO_DESCUENTO): se pide aqui, antes del viaje.
  if (descuento && !motivo) return { ok: false, campo: 'motivo', error: `Paga ${montoParaVer(monto)} de una tarifa de ${montoParaVer(tarifa)}: escribe el motivo del descuento` };
  const notas = [texto(f.notas)];
  if (descuento) notas.push(`Descuento ${montoParaVer(descuento)} sobre tarifa ${montoParaVer(tarifa)}${motivo ? ': ' + motivo : ''}`);

  const cuerpo = {
    businessId: texto(negocio.id),
    negocioNombre: texto(negocio.nombre),
    monto,
    medio: texto(f.medio) || 'transferencia',
    fechaPago,
    mesCubierto: texto(f.mesCubierto) || fechaPago.slice(0, 7),
    canalActivacion: texto(f.canal) || 'panel',
    notas: notas.filter(Boolean).join(' · ')
  };
  // La tarifa solo viaja si hay descuento: sin ella la API toma tarifa = monto, y una tarifa menor que lo pagado la
  // frena (PAGO_TARIFA), que es lo que pasaria si el cliente paga de mas.
  if (descuento) {
    cuerpo.tarifa = tarifa;
    cuerpo.descuento = descuento;
    cuerpo.motivoDescuento = motivo;
  }
  return { ok: true, cuerpo };
}

/**
 * Que decir cuando "Registrar y activar" no pudo. 404/405 = la ruta todavia no esta publicada en la
 * API (PAGOS-NEXO-ACTIVAR-API-T1): el pago NO se registro y el camino viejo sigue sirviendo.
 */
export function motivoSinActivar(res) {
  const code = Number(res?.code);
  if (code === 404 || code === 405) {
    return { sinPublicar: true, mensaje: 'La activacion en un paso aun no esta publicada en la API. No se registro nada: usa "Registrar pago" y activa la suscripcion en la ficha del negocio.' };
  }
  return { sinPublicar: false, mensaje: texto(res?.error) || 'No se pudo registrar y activar' };
}

/** Hasta cuando quedo activa, leido de la respuesta del API (tolera las formas que puede traer). */
export function activaHasta(res, mesCubierto) {
  const d = res?.data?.data ?? res?.data ?? {};
  const s = d.suscripcion ?? d.subscription ?? d;
  const hasta = texto(s?.endDate ?? s?.end_date ?? s?.EndDate ?? d.activa_hasta ?? d.activaHasta);
  return hasta ? hasta.slice(0, 10) : finDelMes(mesCubierto);
}
